import * as React from "react"
import {somePlaceId} from "../model/journey"

/*
 Google maps JavaScript API doesn't play nice with React so we are manually managing the
 React lifecycle here to handle the async nature of driving fetches and driving directions
 display.  There is also the "lone marker" which is just the initial marker we display when
 we are only supplied a single placeId and obviously can't provide driving directions...
 */

// const evtNames = ["click", "mouseover", "recenter", "dragend"]
//
// const camelize = (str: string) => {
//   return str.split(" ").map((word: string) => {
//     return word.charAt(0).toUpperCase() + word.slice(1)
//   }).join("")
// }

type Waypoints = Array<string>

interface OwnProps {
  origin: string
  destination: string
  waypoints: Waypoints
  optimizeWaypoints: boolean
  onDirections: (status: string, directionsRoute: any) => void
  defaultLocation: any
  countryCode: string
}

interface MapProps {
  google?: any
  map?: any
}

type Props = OwnProps & MapProps

interface State {
  directionsResponse: any
  directionsRenderer: any
  loneMarkerPlace: any
  marker: any
}

// We want to fetch after mounting but these props will be undefined unless:
// Directions.defaultProps = {
//   origin: null,
//   destination: null,
//   waypoints: [],
// }

export class Directions extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      directionsResponse: null,
      directionsRenderer: null,

      loneMarkerPlace: null,
      marker: null,
    }
  }

  componentDidMount() {
    const {google, defaultLocation} = this.props

    if (google !== null) {
      this.setState({
        directionsRenderer: new google.maps.DirectionsRenderer(),
        marker: new google.maps.Marker({position: defaultLocation}),
      })
    } else {
      throw new Error("Could not make driving directions renderer.")
    }
    this.fetch()
  }

  // For when they select a different locale
  componentWillReceiveProps(nextProps: Props) {
    const {map, defaultLocation} = this.props

    if (map !== null) {
      const nextLocation = nextProps.defaultLocation
      if (nextLocation.lat !== defaultLocation.lat || nextLocation.lng !== defaultLocation.lng) {
        map.setCenter(nextLocation)
      }
    }
  }

  fetchPlaceDetails(placeId: string) {
    const {map, google} = this.props
    const {marker} = this.state

    if (google !== null && map !== null && marker !== null) {
      const service = new google.maps.places.PlacesService(map)
      service.getDetails(
        {
          placeId,
        },
        (place: any, status: any) => {
          if (status === "OK") {
            this.setState({loneMarkerPlace: place})
          } else {
            throw new Error("Could not fetch google map lone marker place details: " + status)
          }
        },
      )
    }
  }

  fetchDirections(origin: string, destination: string, waypoints: Waypoints) {
    const {google, onDirections, optimizeWaypoints, countryCode} = this.props

    if (google !== null) {
      const service = new google.maps.DirectionsService()
      service.route(
        {
          origin: {placeId: origin},
          destination: {placeId: destination},
          travelMode: "DRIVING",
          waypoints: waypoints.map((id) => ({location: {placeId: id}, stopover: true})),
          optimizeWaypoints,
          region: countryCode,
        },
        (response: any, status: string) => {
          if (status === "OK") {
            this.setState({directionsResponse: response})
          }
          // We are not setting `provideRouteAlternatives: true` above so there can be only one route.
          onDirections(status, status === "OK" ? response.routes[0] : null)
        },
      )
    } else {
      throw new Error("Could not fetch driving directions.")
    }
  }

  fetch() {
    const {origin, destination, waypoints} = this.props

    if (somePlaceId(origin) && somePlaceId(destination)) {
      this.fetchDirections(origin, destination, waypoints)
    } else if (somePlaceId(origin)) {
      this.fetchPlaceDetails(origin)
    } else if (somePlaceId(destination)) {
      this.fetchPlaceDetails(destination)
    }
  }

  clearDirections() {
    const {directionsRenderer, marker} = this.state

    if (directionsRenderer !== null) {
      directionsRenderer.setMap(null)
    }

    if (marker !== null) {
      marker.setMap(null)
    }
  }

  // I want to avoid re-fetching when the optimizeWaypoints flips from true to false
  // because we don't need to: we just fetched those precise directions when it flipped
  // from false to true.  This also necessitated moving the props test *after* the state
  // test in componentDidUpdate, so we're right on the border of "tolerable cleverness"...
  wantToFetch = (prev: Props) => {
    return (
      prev.origin !== this.props.origin ||
      prev.destination !== this.props.destination ||
      prev.waypoints.length !== this.props.waypoints.length ||
      this.props.optimizeWaypoints ||
      // Force fetch/render on initial map load.  This ensures an initial lone marker render.
      (!prev.map && this.props.map)
    )
  }

  // On a props chance (ie. places) we want to *fetch* (which is async) but
  // when that fetch completes we call setState and so in this method we *also*
  // handle those state changes by *displaying* the newly fetched lone marker
  // or driving directions.
  componentDidUpdate(prevProps: Props, prevState: State) {
    const {directionsResponse, loneMarkerPlace} = this.state

    if (prevState.loneMarkerPlace !== loneMarkerPlace) {
      this.renderLoneMarker()
    } else if (prevState.directionsResponse !== directionsResponse) {
      this.renderDirections()
    } else if (this.wantToFetch(prevProps)) {
      this.clearDirections()
      this.fetch()
    }
  }

  componentWillUnmount() {
    this.clearDirections()
  }

  renderLoneMarker() {
    const {google, map} = this.props
    const {loneMarkerPlace, marker} = this.state

    if (map !== null && google !== null && marker !== null && loneMarkerPlace !== null) {
      const markerLocation = loneMarkerPlace.geometry.location

      marker.setPosition(markerLocation)
      marker.setTitle(loneMarkerPlace.name)
      marker.setMap(map)
      map.setCenter(markerLocation)
    } else {
      throw new Error("Could not render google map lone marker.")
    }
  }

  renderDirections() {
    const {google, map} = this.props
    const {directionsRenderer, directionsResponse} = this.state

    if (
      map !== null &&
      google !== null &&
      directionsRenderer !== null &&
      directionsResponse !== null
    ) {
      directionsRenderer.setMap(map)
      directionsRenderer.setDirections(directionsResponse)
    } else {
      throw new Error("Could not render driving directions.")
    }
  }

  // If we need to start using event handler on the map itself it's as easy as passing in
  // and onClick (or whatever), see list of events we care about at top of file
  // handleEvent(evt) {
  //   return (e) => {
  //     const evtName = `on${camelize(evt)}`
  //     if (this.props[evtName]) {
  //       this.props[evtName](this.props, this.marker, e)
  //     }
  //   }
  // }

  render() {
    return null
  }
}

// evtNames.forEach((e) => Directions.propTypes[e] = PropTypes.func)



// WEBPACK FOOTER //
// ./src/components/Directions.ts